import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Gift, Lock, ShieldCheck, Clock, ChevronRight } from "lucide-react";

const WalmartSpark = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <g transform="translate(32 32)" fill="currentColor">
      {[0, 60, 120, 180, 240, 300].map((d) => (
        <rect
          key={d}
          x="-3.2"
          y="-22"
          width="6.4"
          height="16"
          rx="3.2"
          transform={`rotate(${d})`}
        />
      ))}
    </g>
  </svg>
);

const SCAM_TICKER = [
  "LIMITED OFFER",
  "USA ONLY",
  "ENDS SOON",
  "VERIFIED REWARD",
  "INSTANT DELIVERY",
  "CLAIM TODAY",
];

export default function BaitForm({ onSubmit }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", zip: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.replace(/\D/g, "").length >= 7 &&
    /^\d{4,6}$/.test(form.zip.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit();
  };

  return (
    <main
      data-testid="bait-page"
      className="min-h-screen bg-gradient-to-b from-[#0071dc] via-[#0058ab] to-[#003d7a] text-white"
    >
      {/* Walmart-style top bar */}
      <header className="bg-[#0053a0] border-b border-white/10">
        <div className="max-w-md mx-auto flex items-center gap-2 px-5 py-3">
          <WalmartSpark className="w-7 h-7 text-[#ffc220]" />
          <span className="font-display font-extrabold text-lg tracking-tight text-white">
            Walmart
          </span>
        </div>
      </header>

      {/* Marquee urgency strip — classic scam tell */}
      <div className="bg-[#ffc220]/15 border-y border-[#ffc220]/30 overflow-hidden">
        <div className="marquee-track flex gap-10 py-2 text-[#ffc220] text-xs font-bold tracking-[0.2em] whitespace-nowrap">
          {[...SCAM_TICKER, ...SCAM_TICKER].map((t, i) => (
            <span key={i} className="flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ffc220] dot-pulse" />
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-8 pb-16">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffc220]/15 border border-[#ffc220]/40 text-[#ffc220] text-[11px] font-semibold tracking-widest mb-5">
            <Clock className="w-3.5 h-3.5" /> EXPIRES IN 9:58
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-3">
            Get a <span className="text-[#ffc220]">$100</span><br />
            Walmart Gift Card
          </h1>
          <p className="text-blue-100 text-sm sm:text-base">
            Enter your details below to claim your <span className="text-[#ffc220] font-semibold">limited-time</span> reward. Available to verified Walmart customers only.
          </p>
        </div>

        {/* Walmart-themed gift card mock */}
        <div className="relative mx-auto mb-8 w-full max-w-xs">
          <div className="aspect-[1.6/1] rounded-2xl bg-gradient-to-br from-[#0071dc] via-[#0058ab] to-[#003d7a] p-5 shadow-2xl shadow-blue-950/50 border border-white/10 flex flex-col justify-between overflow-hidden">
            <div className="absolute -right-6 -bottom-6 opacity-15 text-[#ffc220]">
              <WalmartSpark className="w-40 h-40" />
            </div>
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-2 text-white">
                <WalmartSpark className="w-5 h-5 text-[#ffc220]" />
                <span className="font-display font-bold text-sm tracking-wide">GIFT CARD</span>
              </div>
              <span className="text-[10px] text-[#ffc220] font-extrabold tracking-[0.2em]">WALMART</span>
            </div>
            <div className="relative text-white">
              <p className="font-display text-5xl font-extrabold leading-none">$100</p>
              <p className="text-[11px] tracking-[0.25em] mt-1 text-blue-100/80">VALUE • LIMITED OFFER</p>
            </div>
          </div>
          <div className="absolute -top-3 -right-3 rotate-12 bg-[#ffc220] text-[#0053a0] text-[10px] font-extrabold tracking-widest px-2.5 py-1 rounded shadow-lg">
            HOT!
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="bait-form"
          className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-5 sm:p-6 space-y-4 shadow-2xl shadow-black/30"
        >
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-medium text-slate-200 tracking-wide">Full Name</Label>
            <Input
              id="name"
              data-testid="input-name"
              autoComplete="off"
              value={form.name}
              onChange={update("name")}
              placeholder="Jane Doe"
              className="bg-white/95 text-slate-900 placeholder:text-slate-400 border-0 h-11"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-medium text-slate-200 tracking-wide">Email Address</Label>
            <Input
              id="email"
              data-testid="input-email"
              type="email"
              autoComplete="off"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className="bg-white/95 text-slate-900 placeholder:text-slate-400 border-0 h-11"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-xs font-medium text-slate-200 tracking-wide">Phone Number</Label>
            <Input
              id="phone"
              data-testid="input-phone"
              type="tel"
              autoComplete="off"
              value={form.phone}
              onChange={update("phone")}
              placeholder="(555) 123-4567"
              className="bg-white/95 text-slate-900 placeholder:text-slate-400 border-0 h-11"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zip" className="text-xs font-medium text-slate-200 tracking-wide">ZIP Code</Label>
            <Input
              id="zip"
              data-testid="input-zip"
              inputMode="numeric"
              autoComplete="off"
              value={form.zip}
              onChange={update("zip")}
              placeholder="90210"
              className="bg-white/95 text-slate-900 placeholder:text-slate-400 border-0 h-11"
              required
            />
          </div>

          <Button
            type="submit"
            data-testid="claim-button"
            disabled={!valid}
            className="w-full h-12 bg-[#ffc220] hover:bg-[#ffd351] text-[#0053a0] font-display font-bold text-base tracking-wide rounded-xl group transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            CLAIM MY $100 GIFT CARD
            <ChevronRight className="w-5 h-5 ml-1 transition-transform group-hover:translate-x-1" />
          </Button>

          <div className="flex items-center justify-center gap-4 pt-1 text-[11px] text-blue-100">
            <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" /> Secure</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Verified</span>
            <span className="flex items-center gap-1.5"><Gift className="w-3 h-3" /> Instant</span>
          </div>
        </form>

        {/* Fake testimonials removed */}

      </div>
    </main>
  );
}
